import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPriorities, getBills, getStatements, joinNetwork } from "@/lib/api";
import { PublicThemesStrip } from "@/components/public/PublicThemesStrip";
import { PositionBadge, StageStepper } from "@/lib/bills";
import { useReveal } from "@/lib/useReveal";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

/**
 * Home — the public front door.
 *
 * The page answers three questions in order:
 *   1. Who speaks here?          (hero + mission sentence)
 *   2. What is before the        (bills section)
 *      legislature right now?
 *   3. What can I do about it?   (priorities → join → statements → voices)
 *
 * Design invariants (see /prompts/design.md if adding sections):
 *   - Warm parchment background. No dark UI.
 *   - Two families only: Cormorant Garamond + Source Sans 3.
 *   - Gold used as a seal, never as a fill. Two or three touches per screen.
 *   - Every Support/Oppose badge carries the word + colour, never colour alone.
 *   - Hero has EXACTLY one primary action plus one quiet secondary link.
 *   - No hero photo. No third-party image CDN. Ever.
 */
export default function Home() {
  const { lang } = useLang();
  const [priorities, setPriorities] = useState([]);
  const [bills, setBills] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", zip: "", parish: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      getPriorities(lang),
      // Bills: the three currently before the legislature. The backend
      // guarantees no TEST- rows reach us (see iter38).
      getBills().then((b) => b.slice(0, 3)),
      getStatements().then((s) => s.slice(0, 3)),
    ]).then(([p, b, s]) => {
      setPriorities(p);
      setBills(b);
      setUpdates(s);
    });
  }, [lang]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await joinNetwork({ ...form, source: "homepage-band" });
      toast.success(
        lang === "es"
          ? "Bienvenido. Le enviaremos un correo cuando la legislatura toque un asunto de fe."
          : "You're in. We'll email you when the legislature takes up something touching the faith.",
      );
      setForm({ name: "", email: "", zip: "", parish: "" });
    } catch {
      toast.error(
        lang === "es"
          ? "Ocurrió un error. Inténtelo de nuevo."
          : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-ccc-paper" data-testid="home-page">
      {/* ============================================================
          1. HERO — typographic. Deep plum field, gold rule, one CTA.
             The optional <img> slot below is here so real photography
             (bishops, testimony, parish life) can drop in later without
             a rebuild. NO stock photography. NO AI imagery. Ever.
      ============================================================ */}
      <Hero lang={lang} />

      {/* ============================================================
          2. BEFORE THE LEGISLATURE NOW — promoted to top of page.
      ============================================================ */}
      <BillsSection bills={bills} lang={lang} />

      {/* ============================================================
          3. THREE PRIORITIES — not ten. Link out to the full framework.
      ============================================================ */}
      <PrioritiesSection priorities={priorities} lang={lang} />

      {/* ============================================================
          4. JOIN — placed where it's earned, after the legislation.
      ============================================================ */}
      <JoinBand form={form} setForm={setForm} submit={submit}
                submitting={submitting} lang={lang} />

      {/* ============================================================
          5. RECENT STATEMENTS — the institution's published record.
      ============================================================ */}
      <StatementsSection updates={updates} lang={lang} />

      {/* ============================================================
          6. VOICE OF THE FAITHFUL — themes strip, no mention counts.
      ============================================================ */}
      <PublicThemesStrip />
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Hero
// ────────────────────────────────────────────────────────────
function Hero({ lang }) {
  const ref = useReveal(0.05);
  const isES = lang === "es";
  return (
    <section
      className="relative w-full bg-ccc-plum-deep text-ccc-paper overflow-hidden"
      data-testid="hero"
    >
      {/*
        Image slot — intentionally empty. When real photography exists
        (testimony at the Capitol, the bishops, parish life), drop the
        <img> here with alt copy written by staff, not generated.
      */}
      <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-28 sm:py-36">
        <div ref={ref} className="rise-in max-w-4xl">
          {/* Thin gold rule above the wordmark — the "seal". */}
          <span className="gold-rule mb-6" aria-hidden="true" />
          <div className="eyebrow eyebrow-light mb-8"
               data-testid="hero-eyebrow">
            Colorado Catholic Conference
          </div>
          <h1
            className="font-serif font-bold text-4xl sm:text-6xl leading-[1.06] tracking-[-0.015em] mb-8"
            data-testid="hero-headline"
            style={{ fontSize: "clamp(38px, 6vw, 56px)" }}
          >
            {isES
              ? "La Voz Unida de los Obispos Católicos de Colorado."
              : "The United Voice of the Catholic Bishops of Colorado."}
          </h1>
          <p
            className="text-lg sm:text-xl leading-[1.7] text-ccc-paper/85 mb-12 measure"
            style={{ fontSize: "19px" }}
          >
            {isES
              ? "Defendemos la dignidad de cada persona humana en las políticas públicas de Colorado — arraigados en el Evangelio y la doctrina social católica."
              : "We speak for the dignity of every human person in Colorado's public policy — rooted in the Gospel and the Church's social teaching."}
          </p>

          {/* Exactly one primary action. Plus one quiet link beneath. */}
          <div className="flex flex-col sm:flex-row sm:items-baseline gap-4 sm:gap-8">
            <Link
              to="/legislative-work"
              data-testid="hero-primary"
              className="inline-flex items-center justify-center bg-ccc-gold-light text-ccc-ink px-8 py-3.5 text-base font-semibold uppercase tracking-[0.08em] hover:bg-ccc-paper transition-colors"
              style={{ fontSize: "17px" }}
            >
              {isES ? "Ver dónde estamos" : "See where we stand"}
            </Link>
            <Link
              to="/take-action/join"
              data-testid="hero-secondary"
              className="text-ccc-paper/85 underline underline-offset-4 decoration-ccc-gold-light hover:text-ccc-paper transition-colors"
              style={{ fontSize: "17px" }}
            >
              {isES ? "Recibir actualizaciones" : "Get the updates"}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// Bills
// ────────────────────────────────────────────────────────────
function BillsSection({ bills, lang }) {
  const ref = useReveal();
  const isES = lang === "es";
  return (
    <section
      className="bg-ccc-paper border-b border-ccc-rule py-20 sm:py-24"
      data-testid="bills-section"
    >
      <div ref={ref} className="rise-in max-w-6xl mx-auto px-6 sm:px-10">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <span className="gold-rule mb-4" aria-hidden="true" />
            <div className="eyebrow mb-2">
              {isES ? "En el Capitolio" : "At the Capitol"}
            </div>
            <h2 className="font-serif font-bold text-ccc-ink"
                style={{ fontSize: "clamp(28px, 4vw, 36px)" }}>
              {isES
                ? "Ante la legislatura ahora."
                : "Before the legislature right now."}
            </h2>
          </div>
          <Link
            to="/legislative-work"
            className="text-ccc-plum hover:text-ccc-plum-deep font-semibold underline decoration-ccc-gold underline-offset-4"
            style={{ fontSize: "17px" }}
          >
            {isES ? "El rastreador completo →" : "The full tracker →"}
          </Link>
        </div>

        {bills.length === 0 ? (
          <p className="text-ccc-muted italic"
             style={{ fontSize: "18px" }}
             data-testid="bills-empty">
            {isES
              ? "La sesión legislativa está en receso."
              : "The legislative session is currently in recess."}
          </p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {bills.map((b) => (
              <BillCard key={b.id} bill={b} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function BillCard({ bill }) {
  return (
    <Link
      to={`/legislative-work/${bill.id}`}
      data-testid={`bill-card-${bill.number}`}
      className="block bg-ccc-white border border-ccc-rule hover:border-ccc-plum transition-colors p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <span
          className="font-semibold text-ccc-muted"
          style={{ fontSize: "15px", letterSpacing: "0.04em" }}
        >
          {bill.number}
        </span>
        <PositionBadge position={bill.position} />
      </div>
      <h3
        className="font-serif font-semibold text-ccc-ink mb-5 leading-[1.25]"
        style={{ fontSize: "24px" }}
      >
        {bill.title}
      </h3>
      {bill.issue_areas?.length > 0 && (
        <div className="mb-4">
          <span className="text-ccc-muted" style={{ fontSize: "14px" }}>
            {bill.issue_areas
              .slice(0, 2)
              .map((s) => s.replace(/-/g, " "))
              .join(" · ")}
          </span>
        </div>
      )}
      <StageStepper stage={bill.stage} />
    </Link>
  );
}

// ────────────────────────────────────────────────────────────
// Priorities (three, not ten)
// ────────────────────────────────────────────────────────────
function PrioritiesSection({ priorities, lang }) {
  const ref = useReveal();
  const isES = lang === "es";
  // The Conference organises around ten priorities; the homepage surfaces
  // only the three currently in play so the page has hierarchy rather
  // than a wall of ten equally-weighted items.
  const featured = priorities.slice(0, 3);

  return (
    <section
      className="bg-ccc-white border-b border-ccc-rule py-20 sm:py-24"
      data-testid="priorities-section"
    >
      <div ref={ref} className="rise-in max-w-6xl mx-auto px-6 sm:px-10">
        <div className="mb-14 max-w-2xl">
          <span className="gold-rule mb-4" aria-hidden="true" />
          <div className="eyebrow mb-2">
            {isES ? "Marco Guía" : "Guiding Framework"}
          </div>
          <h2
            className="font-serif font-bold text-ccc-ink mb-4"
            style={{ fontSize: "clamp(28px, 4vw, 36px)" }}
          >
            {isES
              ? "Tres prioridades ante nosotros esta sesión."
              : "Three priorities before us this session."}
          </h2>
          <p className="text-ccc-body measure"
             style={{ fontSize: "18px", lineHeight: 1.7 }}>
            {isES
              ? "El trabajo de la Conferencia está enraizado en diez prioridades permanentes de la doctrina social católica. Estas tres son las que están en juego en la sesión actual."
              : "The Conference's work is rooted in ten enduring priorities of Catholic social teaching. These three are the ones in play in the current session."}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {featured.map((p) => (
            <Link
              key={p.id}
              to={`/priorities/${p.slug}`}
              data-testid={`priority-featured-${p.slug}`}
              className="group flex flex-col bg-ccc-paper border border-ccc-rule hover:border-ccc-plum p-8 transition-colors"
            >
              <span
                className="font-serif font-bold text-ccc-gold mb-4"
                style={{ fontSize: "46px", lineHeight: 1 }}
              >
                {p.order || ""}
              </span>
              <h3
                className="font-serif font-semibold text-ccc-ink mb-3 leading-[1.25]"
                style={{ fontSize: "24px" }}
              >
                {p.title}
              </h3>
              <p className="text-ccc-body"
                 style={{ fontSize: "17px", lineHeight: 1.6 }}>
                {p.summary}
              </p>
            </Link>
          ))}
        </div>

        <div className="mt-10">
          <Link
            to="/priorities"
            data-testid="priorities-all"
            className="text-ccc-plum hover:text-ccc-plum-deep font-semibold underline decoration-ccc-gold underline-offset-4"
            style={{ fontSize: "17px" }}
          >
            {isES
              ? "Las diez prioridades católicas →"
              : "All ten Catholic priorities →"}
          </Link>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// Join
// ────────────────────────────────────────────────────────────
function JoinBand({ form, setForm, submit, submitting, lang }) {
  const ref = useReveal();
  const isES = lang === "es";
  return (
    <section
      className="bg-ccc-plum-deep text-ccc-paper py-20 sm:py-24"
      data-testid="join-band"
    >
      <div ref={ref} className="rise-in max-w-6xl mx-auto px-6 sm:px-10 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
        <div>
          <span className="gold-rule mb-4" aria-hidden="true" />
          <div className="eyebrow eyebrow-light mb-3">
            {isES ? "Enterese" : "Be told"}
          </div>
          <h2
            className="font-serif font-bold mb-5 leading-[1.15]"
            style={{ fontSize: "clamp(28px, 4vw, 36px)" }}
          >
            {isES
              ? "Le avisaremos cuando la legislatura toque un asunto de fe."
              : "We'll tell you when the legislature takes up something touching the faith."}
          </h2>
          <p className="text-ccc-paper/80 measure"
             style={{ fontSize: "18px", lineHeight: 1.7 }}>
            {isES
              ? "Enviamos actualizaciones concisas cuando el trabajo del Capitolio requiere una voz católica — con el proyecto de ley, la posición de la Conferencia y cómo hacerse oír. Sin ruido, sin bombardeo, sin venderemos su información."
              : "Concise updates when the Capitol's work asks for a Catholic voice — with the bill, the Conference's position, and how to be heard. No noise, no barrage, no data sold."}
          </p>
        </div>

        <form onSubmit={submit} className="grid grid-cols-2 gap-3"
              data-testid="join-form">
          <div className="col-span-2">
            <label htmlFor="join-name" className="block eyebrow eyebrow-light mb-2">
              {isES ? "Nombre" : "Name"}
            </label>
            <input
              id="join-name"
              required
              data-testid="join-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border border-ccc-paper/30 text-ccc-paper px-4 py-3 focus:outline-none focus:border-ccc-gold-light"
              style={{ fontSize: "18px" }}
            />
          </div>
          <div className="col-span-2">
            <label htmlFor="join-email" className="block eyebrow eyebrow-light mb-2">
              {isES ? "Correo electrónico" : "Email"}
            </label>
            <input
              id="join-email"
              required
              type="email"
              data-testid="join-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent border border-ccc-paper/30 text-ccc-paper px-4 py-3 focus:outline-none focus:border-ccc-gold-light"
              style={{ fontSize: "18px" }}
            />
          </div>
          <div>
            <label htmlFor="join-zip" className="block eyebrow eyebrow-light mb-2">
              ZIP
            </label>
            <input
              id="join-zip"
              data-testid="join-zip"
              value={form.zip}
              onChange={(e) => setForm({ ...form, zip: e.target.value })}
              className="w-full bg-transparent border border-ccc-paper/30 text-ccc-paper px-4 py-3 focus:outline-none focus:border-ccc-gold-light"
              style={{ fontSize: "18px" }}
            />
          </div>
          <div>
            <label htmlFor="join-parish" className="block eyebrow eyebrow-light mb-2">
              {isES ? "Parroquia" : "Parish"}
            </label>
            <input
              id="join-parish"
              data-testid="join-parish"
              value={form.parish}
              onChange={(e) => setForm({ ...form, parish: e.target.value })}
              className="w-full bg-transparent border border-ccc-paper/30 text-ccc-paper px-4 py-3 focus:outline-none focus:border-ccc-gold-light"
              style={{ fontSize: "18px" }}
            />
          </div>
          <div className="col-span-2 mt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="join-submit"
              className="w-full bg-ccc-gold-light text-ccc-ink px-8 py-3.5 font-semibold uppercase tracking-[0.08em] hover:bg-ccc-paper transition-colors disabled:opacity-60"
              style={{ fontSize: "17px" }}
            >
              {submitting
                ? (isES ? "Enviando…" : "Sending…")
                : (isES ? "Recibir actualizaciones" : "Get the updates")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────
// Recent statements
// ────────────────────────────────────────────────────────────
function StatementsSection({ updates, lang }) {
  const ref = useReveal();
  const isES = lang === "es";

  const dateLine = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(isES ? "es-CO" : "en-US", {
        year: "numeric", month: "long", day: "numeric",
      });
    } catch { return iso; }
  };

  return (
    <section className="bg-ccc-paper py-20 sm:py-24" data-testid="statements-section">
      <div ref={ref} className="rise-in max-w-6xl mx-auto px-6 sm:px-10">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <span className="gold-rule mb-4" aria-hidden="true" />
            <div className="eyebrow mb-2">
              {isES ? "Registro Publicado" : "The Record"}
            </div>
            <h2 className="font-serif font-bold text-ccc-ink"
                style={{ fontSize: "clamp(28px, 4vw, 36px)" }}>
              {isES ? "Declaraciones recientes." : "Recent statements."}
            </h2>
          </div>
          <Link
            to="/resources/statements"
            className="text-ccc-plum hover:text-ccc-plum-deep font-semibold underline decoration-ccc-gold underline-offset-4"
            style={{ fontSize: "17px" }}
          >
            {isES ? "Biblioteca completa →" : "The full library →"}
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {updates.map((u) => (
            <Link
              key={u.id}
              to={`/resources/statements/${u.slug}`}
              data-testid={`statement-card-${u.slug}`}
              className="block bg-ccc-white border border-ccc-rule hover:border-ccc-plum transition-colors p-6"
            >
              <div className="eyebrow mb-3">{dateLine(u.published_at)}</div>
              <h3
                className="font-serif font-semibold text-ccc-ink mb-3 leading-[1.25]"
                style={{ fontSize: "22px" }}
              >
                {u.title}
              </h3>
              <p
                className="text-ccc-body line-clamp-3"
                style={{ fontSize: "17px", lineHeight: 1.6 }}
              >
                {u.excerpt}
              </p>
              <div
                className="mt-4 text-ccc-muted uppercase tracking-[0.14em] font-semibold"
                style={{ fontSize: "14px" }}
              >
                {u.type}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
